/**
 * プロフィール入力のサブステップ進捗バー（垂直タイムライン形式）
 *
 * CreateUserWizardのProfileSubStepBarと同じデザイン
 * 6ステップすべてを表示:
 * - 完了: チェックアイコン（青）
 * - 現在: 実心の丸（ハイライト）
 * - 未完了: 空心の丸
 */
import I18n from "features/shared/i18n";
import { PlayerProfileSubStep, PlayerFavoritesSubStep, ALL_PLAYER_PROFILE_STEPS } from "../types";

// カラー定数
const COLORS = {
  completed: "#70b1f4", // 青（btn-blueと同じ）
  current: "#3f4a61", // 現在ステップ
  pending: "#c2c9cf", // 未完了
  line: "#e0e4e7", // 接続線
};

// サイズ定数
const DOT_SIZE = 32;
const LINE_WIDTH = 2;
const LINE_HEIGHT = 32;

// アイコンステップのID
const ICON_STEP_ID = "icon";
// アイコンステップ以外の全ステップID
const NON_ICON_STEP_IDS = ALL_PLAYER_PROFILE_STEPS.filter((s) => s.id !== ICON_STEP_ID).map(
  (s) => s.id,
);

interface PlayerProfileSubStepBarProps {
  currentSubStep: PlayerProfileSubStep;
  favoritesSubStep?: PlayerFavoritesSubStep;
  onStepClick?: (
    profileSubStep: PlayerProfileSubStep,
    favoritesSubStep?: PlayerFavoritesSubStep,
  ) => void;
  completedStepIds?: string[];
  isGenerating?: boolean;
}

const getCurrentStepIndex = (
  currentSubStep: PlayerProfileSubStep,
  favoritesSubStep?: PlayerFavoritesSubStep,
): number => {
  return ALL_PLAYER_PROFILE_STEPS.findIndex((step) => {
    if (step.profileSubStep !== currentSubStep) return false;
    if (step.favoritesSubStep) {
      return step.favoritesSubStep === favoritesSubStep;
    }
    return true;
  });
};

export const PlayerProfileSubStepBar = ({
  currentSubStep,
  favoritesSubStep,
  onStepClick,
  completedStepIds,
  isGenerating,
}: PlayerProfileSubStepBarProps) => {
  const currentIndex = getCurrentStepIndex(currentSubStep, favoritesSubStep);
  const steps = ALL_PLAYER_PROFILE_STEPS;

  // すべての非アイコンステップが完了しているかどうか
  const allNonIconStepsCompleted = completedStepIds
    ? NON_ICON_STEP_IDS.every((id) => completedStepIds.includes(id))
    : false;

  // ステップがクリック可能かどうかを判定
  const isStepClickable = (index: number, stepId: string): boolean => {
    if (!onStepClick) return false;
    if (index === currentIndex) return false;
    if (isGenerating) return false;

    if (completedStepIds) {
      // completedStepIdsモード
      if (stepId === ICON_STEP_ID) {
        // アイコンステップ: 他の全ステップが完了済みの場合のみクリック可能
        return allNonIconStepsCompleted;
      }
      // 非アイコンステップ: 常にクリック可能
      return true;
    }

    // 位置ベースモード: 現在より前のステップのみ
    return index < currentIndex;
  };

  const handleStepClick = (index: number, stepId: string) => {
    if (!isStepClickable(index, stepId)) return;
    const step = steps[index];
    onStepClick!(step.profileSubStep, step.favoritesSubStep);
  };

  return (
    <div className="flex-col-el py-6 pr-8">
      {steps.map((step, index) => {
        const isCurrent = index === currentIndex;
        // 現在のステップの場合は完了扱いにしない
        const isCompleted = isCurrent
          ? false
          : completedStepIds
            ? completedStepIds.includes(step.id)
            : index < currentIndex;
        const isLast = index === steps.length - 1;
        const clickable = isStepClickable(index, step.id);

        return (
          <div key={step.id} className="flex-row-el">
            {/* 左側：アイコンと接続線 */}
            <div className="flex-col-el items-center" style={{ width: DOT_SIZE }}>
              {/* ドット/アイコン */}
              <div
                className={`flex-col-el flex-center rounded-full ${clickable ? "cursor-pointer" : ""}`}
                style={{
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  backgroundColor: isCompleted
                    ? COLORS.completed
                    : isCurrent
                      ? COLORS.current
                      : "transparent",
                  border:
                    !isCompleted && !isCurrent ? `${LINE_WIDTH}px solid ${COLORS.pending}` : "none",
                }}
                onClick={() => handleStepClick(index, step.id)}
              >
                {isCompleted && (
                  <span
                    className="material-symbols-outlined text-[20px]"
                    style={{ color: "#FFFFFF" }}
                  >
                    check
                  </span>
                )}
                {isCurrent && (
                  <div
                    className="rounded-full"
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: "#FFFFFF",
                    }}
                  />
                )}
              </div>
              {/* 接続線 */}
              {!isLast && (
                <div
                  style={{
                    width: LINE_WIDTH,
                    height: LINE_HEIGHT,
                    backgroundColor: isCompleted ? COLORS.completed : COLORS.line,
                  }}
                />
              )}
            </div>

            {/* 右側：ラベル */}
            <p
              className={`text-body text-[24px] whitespace-nowrap !ml-3 ${clickable ? "cursor-pointer" : ""}`}
              style={{
                color: isCompleted ? COLORS.completed : isCurrent ? COLORS.current : COLORS.pending,
                lineHeight: `${DOT_SIZE}px`,
              }}
              onClick={() => handleStepClick(index, step.id)}
            >
              {I18n.t(step.labelKey)}
            </p>
          </div>
        );
      })}
    </div>
  );
};
